import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Tooltip, Stack, Select, MenuItem } from '@mui/material';
import { alpha } from '@mui/material/styles';
import api from '../../services/api';
import { holidaysSet, toYyyyMmDd } from '../../utils/businessDays';

const weekdays = ['M','T','W','T','F','S','S'];

function MonthGrid({ year, monthIndex, holidaySet, holidayNameMap, takenMap, draft, onRangeSelected, onHintChange, selectStart, setSelectStart }) {

  const first = new Date(year, monthIndex, 1);
  const last = new Date(year, monthIndex + 1, 0);
  const days = [];
  for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
    days.push(new Date(d));
  }
  const startOffset = (first.getDay() + 6) % 7; // Monday=0
  const rows = [];
  const todayYmd = toYyyyMmDd(new Date());
  const draftStart = draft?.startDate;
  const draftDays = Number(draft?.days || 0);
  let draftSet = new Set();
  if (draftStart && draftDays > 0) {
    // build local set of selected business days
    const start = new Date(draftStart);
    let d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    let count = 0;
    while (true) {
      const ymd = toYyyyMmDd(d);
      const dow = d.getDay();
      const isWeekend = dow === 0 || dow === 6;
      const isHol = holidaySet.has(ymd);
      if (!isWeekend && !isHol) {
        draftSet.add(ymd);
        count++;
        if (count >= draftDays) break;
      }
      d.setDate(d.getDate() + 1);
    }
  }
  let row = new Array(startOffset).fill(null);
  days.forEach(date => {
    row.push(new Date(date));
    if (row.length === 7) { rows.push(row); row = []; }
  });
  if (row.length) { while (row.length < 7) row.push(null); rows.push(row); }

  return (
    <Box sx={{ border: theme=>`1px solid ${theme.palette.divider}`, borderRadius:1, p:1, bgcolor:'background.paper', color:'text.primary' }}>
      <Typography variant="subtitle2" sx={{ mb:1 }}>
        {first.toLocaleString('en-US', { month:'long' })} {year}
      </Typography>
      <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:0.5 }}>
        {weekdays.map((w, i) => (
          <Box key={i} sx={{ textAlign:'center', fontWeight:700, border: theme=>`1px solid ${theme.palette.divider}`, bgcolor:'action.hover' }}>{w}</Box>
        ))}
        {rows.map((r, ri) => r.map((cell, ci) => {
          if (!cell) return <Box key={`${ri}-${ci}`} sx={{ border: theme=>`1px solid ${theme.palette.divider}`, height:28 }} />;
          const ymd = toYyyyMmDd(cell);
          const isWeekend = ci >= 5;
          const isHoliday = holidaySet.has(ymd);
          const isToday = ymd === todayYmd;
          const isDraft = draftSet.has(ymd);
          const info = takenMap.get(ymd); // {kind,id,startYmd,endYmd}
          const consumedKind = info?.kind;
          let bg = 'background.paper';
          // Holidays keep special color; weekends have their own color; otherwise paper
          if (isHoliday) bg = (theme)=> theme.palette.error.light; // holiday accent via theme
          else if (isWeekend) bg = (theme)=> (theme.palette.mode==='dark' ? alpha(theme.palette.primary.main, 0.18) : theme.palette.action.selected); // stronger contrast in dark
          if (consumedKind === 'approved') bg = (theme)=> theme.palette.success.main; // approved
          else if (consumedKind === 'pending') bg = (theme)=> theme.palette.info.main; // pending
          // Draft selection: yellow via warning
          if (isDraft) bg = (theme)=> theme.palette.warning.light;
          const style = {
            border: theme=>`1px solid ${theme.palette.divider}`, height:28, textAlign:'center', lineHeight:'28px',
            bgcolor: bg, fontWeight: isHoliday ? 700 : 400, color: isHoliday ? 'primary.contrastText' : 'text.primary',
            outline: (theme)=> isToday ? `2px solid ${theme.palette.success.main}` : 'none'
          };
          let tip = '';
          if (isHoliday) tip = holidayNameMap.get(ymd) || 'Holiday';
          else if (info) tip = `${info.kind==='approved'?'Approved':'Pending'} leave #${info.id}: ${info.startYmd} → ${info.endYmd}`;
          else if (isDraft) tip = 'Draft selection';
          const onLeftClick = () => {
            if (!isHoliday && !isWeekend) {
              if (!selectStart) {
                setSelectStart(ymd);
                onHintChange && onHintChange('Select an end date for the range');
              } else {
                const start = selectStart < ymd ? selectStart : ymd;
                const end = selectStart < ymd ? ymd : selectStart;
                setSelectStart(null);
                onHintChange && onHintChange('');
                onRangeSelected && onRangeSelected({ startYmd: start, endYmd: end });
              }
            }
          };
          const onRightClick = (e) => {
            e.preventDefault();
            // Cancel in-progress range
            if (selectStart) {
              setSelectStart(null);
              onHintChange && onHintChange('');
            }
            // Clear drafted period globally (unhighlight)
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('hrapp.leave.refresh'));
            }
          };
          return (
            <Tooltip key={`${ri}-${ci}`} title={tip} arrow>
              <Box sx={{ ...style, outline: (theme)=> selectStart===ymd ? `2px solid ${theme.palette.success.main}` : style.outline, bgcolor: selectStart===ymd ? ((theme)=> theme.palette.success.light) : style.bgcolor }}
                   onClick={onLeftClick}
                   onContextMenu={onRightClick}
                   style={{ cursor: (!isHoliday && !isWeekend) ? 'pointer':'default' }}>{cell.getDate()}</Box>
            </Tooltip>
          );
        }))}
      </Box>
    </Box>
  );
}

export default function YearCalendar({ year, draft }) {
  const [holidays, setHolidays] = useState([]);
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState('');
  const [selectStart, setSelectStart] = useState(null);

  const [y, setY] = useState(year || new Date().getFullYear());
  const [monthsPerRow, setMonthsPerRow] = useState(()=> Number(sessionStorage.getItem('hrapp_months_per_row')||6));
  const [hint, setHint] = useState('');

  useEffect(() => { setY(year || new Date().getFullYear()); }, [year]);

  useEffect(() => {
    (async () => {
      try {
        // Use backend enriched holidays (DB + Enrico observed)
        let merged;
        try {
          // Canonical lowercase endpoint with backward-compat at API level
          merged = await api.get('/holidays/enriched', { params: { year: y } });
        } catch (err) {
          if (err?.response?.status === 404) {
            merged = await api.get('/holidays');
          } else {
            throw err;
          }
        }
        setHolidays(Array.isArray(merged)?merged:[]);
      } catch (e) { setError(e?.response?.data || e.message || 'Failed to load holidays'); }
      try {
        const mine = await api.get('/leave-requests/mine');
        setRequests(Array.isArray(mine)?mine:[]);
      } catch { /* ignore */ }
    })();
  }, [y]);

  const hset = useMemo(() => holidaysSet(holidays), [holidays]);
  const hmap = useMemo(() => {
    const m = new Map();
    (holidays||[]).forEach(h => {
      const key = (h.date || h.Date || '').toString().substring(0,10);
      const name = h.localName || h.name || h.Name || h.LocalName;
      if (key) m.set(key, name);
    });
    return m;
  }, [holidays]);

  const takenMap = useMemo(() => {
    // Map YMD -> info about the leave request covering that business day
    const map = new Map(); // ymd -> { kind: 'approved'|'pending', id, startYmd, endYmd }
    const filtered = (requests||[]).filter(r => {
      const sdYmd = (r.startDateLocalYmd || r.StartDateLocalYmd) ?? toYyyyMmDd(new Date(r.startDate||r.StartDate));
      const sd = new Date(sdYmd);
      return sd.getFullYear() === y;
    });
    const h = hset;
    filtered.forEach(r => {
      const startYmd = (r.startDateLocalYmd || r.StartDateLocalYmd) ?? toYyyyMmDd(new Date(r.startDate||r.StartDate));
      const reqDays = Number(r.businessDays || r.BusinessDays || 0);
      // Fallback to endYmd-based count if reqDays missing
      const endYmd = (r.endDateLocalYmd || r.EndDateLocalYmd) ?? toYyyyMmDd(new Date(r.endDate||r.EndDate));
      const start = new Date(startYmd);
      let d = new Date(start);
      let marked = 0;
      // Mark exactly the recorded number of business days to avoid TZ/off-by-one issues
      while (marked < (reqDays > 0 ? reqDays : Number.POSITIVE_INFINITY)) {
        const ymd = toYyyyMmDd(d);
        const dow = d.getDay();
        const isWeekend = dow === 0 || dow === 6;
        const isHoliday = h.has(ymd);
        if (!isWeekend && !isHoliday) {
          const status = (r.status||r.Status);
          const kind = status === 'Approved' ? 'approved'
            : ((status === 'Pending' || status === 'PendingDM' || status === 'PendingBoard') ? 'pending' : undefined);
          if (kind) map.set(ymd, { kind, id: (r.id||r.Id), startYmd, endYmd });
          marked++;
          if (reqDays > 0 && marked >= reqDays) break;
        }
        // Safety: if reqDays is missing, stop at endYmd inclusive
        if (!(reqDays > 0) && ymd === endYmd) break;
        d.setDate(d.getDate()+1);
      }
    });
    return map;
  }, [requests, hset, y]);


  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb:1 }}>
        <Typography variant="h6" sx={{ flex:1 }}>Calendar {y}</Typography>
        {hint && <Typography variant="body2" color="success.main">{hint}</Typography>}
        <Select size="small" value={y} onChange={(e)=>setY(Number(e.target.value))}>
          {Array.from({length: 5}).map((_,i)=>{
            const yr = new Date().getFullYear() - 2 + i;
            return <MenuItem key={yr} value={yr}>{yr}</MenuItem>;
          })}
        </Select>
        <Select size="small" value={monthsPerRow} onChange={(e)=>{ const v=Number(e.target.value); setMonthsPerRow(v); sessionStorage.setItem('hrapp_months_per_row', String(v)); }}>
          {[3,4,6].map(n => <MenuItem key={n} value={n}>{n} / row</MenuItem>)}
        </Select>
      </Stack>
      {error && <Typography color="error" sx={{ mb:1 }}>{error}</Typography>}
      <Box sx={{ display:'grid', gridTemplateColumns:`repeat(${monthsPerRow}, 1fr)`, gap:2 }}>
        {Array.from({ length: 12 }).map((_, i) => (
          <MonthGrid key={i} year={y} monthIndex={i} holidaySet={hset} holidayNameMap={hmap} takenMap={takenMap} draft={draft}
            selectStart={selectStart} setSelectStart={setSelectStart}
            onHintChange={setHint}
            onRangeSelected={({startYmd,endYmd})=>{
              // prevent overlaps with approved/pending
              const blocked = [];
              const start = new Date(startYmd);
              const end = new Date(endYmd);
              for(let d=new Date(start); d<=end; d.setDate(d.getDate()+1)){
                const ymd = toYyyyMmDd(d);
                const info = takenMap.get(ymd);
                if (info && (info.kind==='approved' || info.kind==='pending')) { blocked.push(ymd); break; }
              }
              if (blocked.length){
                const ev2 = new CustomEvent('hrapp.dialog', { detail:{ title:'Overlapping leave request', message:'The selected period overlaps with an existing pending or approved request.', severity:'warning' }});
                window.dispatchEvent(ev2);
                return;
              }
              const days = (()=>{
                let c=0; for(let d=new Date(start); d<=end; d.setDate(d.getDate()+1)){
                  const dow=d.getDay(); const ymd=toYyyyMmDd(d); if((dow!==0&&dow!==6)&&!hset.has(ymd)) c++; }
                return c;})();
              const ev = new CustomEvent('hrapp.calendar.range', { detail:{ startYmd, endYmd, days }});
              window.dispatchEvent(ev);
              // Auto-open dialog for new request
              const ev2 = new CustomEvent('hrapp.open.request.dialog', { detail:{ startYmd, days }});
              window.dispatchEvent(ev2);
            }} />
        ))}
      </Box>
    </Box>
  );
}

